// User Roles
export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  HR_MANAGER: 'hr_manager',
  HR_EXECUTIVE: 'hr_executive',
  EMPLOYEE: 'employee',
  CUSTOMER: 'customer',
  SALES_MANAGER: 'sales_manager',
  SALES_EXECUTIVE: 'sales_executive',
  DISPATCH_MANAGER: 'dispatch_manager',
  SUPPORT_MANAGER: 'support_manager',
  TRAINEE: 'trainee'
};

// Role Hierarchy (higher number = more permissions)
export const ROLE_HIERARCHY = {
  [USER_ROLES.SUPER_ADMIN]: 5,
  [USER_ROLES.HR_MANAGER]: 4,
  [USER_ROLES.HR_EXECUTIVE]: 3,
  [USER_ROLES.SALES_MANAGER]: 3,
  [USER_ROLES.DISPATCH_MANAGER]: 3,
  [USER_ROLES.SUPPORT_MANAGER]: 3,
  [USER_ROLES.SALES_EXECUTIVE]: 2,
  [USER_ROLES.EMPLOYEE]: 1,
  [USER_ROLES.TRAINEE]: 1,
  [USER_ROLES.CUSTOMER]: 0
};

// Organizational Role Assignment Hierarchy
// Defines which roles can assign which other roles based on organizational structure
// Super Admin
// │
// ├── Super Admin (can assign other Super Admins)
// ├── HR Manager
// │   └── HR Executive
// │       └── Employee
// │
// └── Sales Manager
//     ├── Sales Executive
//     ├── Customer
//     ├── Dispatch Manager
//     └── Support Manager
export const ROLE_ASSIGNMENT_HIERARCHY = {
  [USER_ROLES.SUPER_ADMIN]: [
    USER_ROLES.SUPER_ADMIN, // Super Admin can assign other Super Admins
    USER_ROLES.HR_MANAGER,
    USER_ROLES.HR_EXECUTIVE,
    USER_ROLES.EMPLOYEE,
    USER_ROLES.SALES_MANAGER,
    USER_ROLES.SALES_EXECUTIVE,
    USER_ROLES.CUSTOMER,
    USER_ROLES.DISPATCH_MANAGER,
    USER_ROLES.SUPPORT_MANAGER,
    USER_ROLES.TRAINEE
  ],
  [USER_ROLES.HR_MANAGER]: [
    USER_ROLES.HR_EXECUTIVE,
    USER_ROLES.EMPLOYEE,
    USER_ROLES.TRAINEE
  ],
  [USER_ROLES.HR_EXECUTIVE]: [
    USER_ROLES.EMPLOYEE,
    USER_ROLES.TRAINEE
  ],
  [USER_ROLES.SALES_MANAGER]: [
    USER_ROLES.SALES_EXECUTIVE,
    USER_ROLES.CUSTOMER,
    USER_ROLES.DISPATCH_MANAGER,
    USER_ROLES.SUPPORT_MANAGER
  ],
  // Sales Executive, Customer, Dispatch Manager, Support Manager, Employee cannot assign any roles
  [USER_ROLES.SALES_EXECUTIVE]: [],
  [USER_ROLES.CUSTOMER]: [],
  [USER_ROLES.DISPATCH_MANAGER]: [],
  [USER_ROLES.SUPPORT_MANAGER]: [],
  [USER_ROLES.EMPLOYEE]: [],
  [USER_ROLES.TRAINEE]: []
};

// Navigation Items - Updated according to PRD
export const NAVIGATION_ITEMS = {
  // Main Navigation
  DASHBOARD: 'dashboard',
  SYSTEM_ACCESS: 'system_access',
  HRMS: 'hrms',
  CRM_SALES: 'crm_sales',
  ANALYTICS_REPORTING: 'analytics_reporting',
  EMPLOYEE_SELF_SERVICE: 'employee_self_service',

  // System & Access
  USERS_ROLES: 'users_roles',
  MFA_POLICY: 'mfa_policy',
  SESSION_SETTINGS: 'session_settings',
  AUDIT_LOGS: 'audit_logs',

  // HRMS Modules
  RECRUITMENT: 'recruitment',
  ONBOARDING: 'onboarding',
  EMPLOYEE_DATABASE: 'employee_database',
  ATTENDANCE_LEAVE: 'attendance_leave',
  PAYROLL: 'payroll',
  PERFORMANCE: 'performance',
  TRAINING: 'training',
  EXIT_MANAGEMENT: 'exit_management',
  COMPLIANCE_POLICY: 'compliance_policy',

  // Recruitment Sub-modules
  JOB_POSTS: 'job_posts',
  CANDIDATE_PIPELINE: 'candidate_pipeline',
  INTERVIEW_SCHEDULING: 'interview_scheduling',
  OFFER_MANAGEMENT: 'offer_management',

  // Onboarding Sub-modules
  DOCUMENT_VERIFICATION: 'document_verification',

  BACKGROUND_VERIFICATION: 'background_verification',
  ORIENTATION: 'orientation',

  // Employee Database Sub-modules
  EMPLOYEE_LIST: 'employee_list',
  ADD_EMPLOYEE: 'add_employee',
  EMPLOYEE_PROFILE: 'employee_profile',
  TRANSFERS: 'transfers',
  BULK_UPLOAD: 'bulk_upload',

  // Attendance & Leave Sub-modules
  ATTENDANCE_LOGS: 'attendance_logs',
  TIMESHEET: 'timesheet',
  REGULARIZATION: 'regularization',
  LEAVE_REQUESTS: 'leave_requests',
  LEAVE_POLICIES: 'leave_policies',
  HOLIDAY_CALENDAR: 'holiday_calendar',
  SHIFT_ROSTER: 'shift_roster',

  // Payroll Sub-modules
  PAYROLL_DASHBOARD: 'payroll_dashboard',
  SALARY_STRUCTURE: 'salary_structure',
  PAYROLL_RUN: 'payroll_run',
  PAYSLIPS: 'payslips',
  STATUTORY_COMPLIANCE: 'statutory_compliance',
  FULL_FINAL: 'full_final',

  // Performance Sub-modules
  PERFORMANCE_CYCLES: 'performance_cycles',
  GOAL_SETTING: 'goal_setting',
  REVIEWS: 'reviews',
  CALIBRATION: 'calibration',
  OUTCOMES: 'outcomes',

  // Training Sub-modules
  TRAINING_CATALOG: 'training_catalog',
  LEARNING_PATHS: 'learning_paths',
  SESSIONS: 'sessions',
  CERTIFICATES: 'certificates',
  COMPLIANCE: 'compliance',

  // Exit Management Sub-modules
  EXIT_REQUESTS: 'exit_requests',
  NO_DUES: 'no_dues',
  RELIEVING_LETTER: 'relieving_letter',
  EXPERIENCE_CERTIFICATE: 'experience_certificate',

  // Compliance & Policy Sub-modules
  POLICY_LIBRARY: 'policy_library',
  ACKNOWLEDGMENTS: 'acknowledgments',

  // CRM / Sales Modules
  LEAD_MANAGEMENT: 'lead_management',
  OPPORTUNITIES_PIPELINE: 'opportunities_pipeline',
  QUOTATIONS_PROPOSALS: 'quotations_proposals',
  ORDERS_LOGISTICS: 'orders_logistics',
  CUSTOMERS: 'customers',
  CAMPAIGNS_INTEGRATIONS: 'campaigns_integrations',
  REPORTS_ANALYTICS: 'reports_analytics',
  CRM_SETTINGS: 'crm_settings',

  // Lead Management Sub-modules
  LEAD_DASHBOARD: 'lead_dashboard',
  LEAD_SOURCES_CAPTURE: 'lead_sources_capture',
  LEAD_LIST_FILTERS: 'lead_list_filters',
  LEAD_DETAIL_360: 'lead_detail_360',
  LEAD_ACTIVITIES: 'lead_activities',
  LEAD_ASSIGNMENT: 'lead_assignment',
  LEAD_SCORING_QUALIFICATION: 'lead_scoring_qualification',
  CONVERT_OPPORTUNITY: 'convert_opportunity',

  // Opportunities & Pipeline Sub-modules
  PIPELINE_DASHBOARD: 'Opportunity-dashboard',
  KANBAN_STAGES: 'kanban_stages',
  OPPORTUNITY_DETAIL: 'opportunity_detail',
  TASKS_FOLLOWUPS: 'tasks_followups',
  CLOSE_WON_LOST: 'close_won_lost',
  FORECASTS_TARGETS: 'forecasts_targets',
  PIPELINE_SETTINGS: 'pipeline_settings',

  // Quotations & Proposals Sub-modules
  QUOTATION_DASHBOARD: 'quotation_dashboard',
  QUOTE_BUILDER: 'quote_builder',
  SEND_TRACK: 'send_track',
  VERSION_HISTORY: 'version_history',
  QUOTATION_SETTINGS: 'quotation_settings',

  // Orders & Logistics Sub-modules
  ORDERS_BOARD: 'orders_board',
  ORDER_DETAIL: 'order_detail',
  DISPATCH_LABELS: 'dispatch_labels',
  TRACKING_EXCEPTIONS: 'tracking_exceptions',
  COURIER_COMPARISON: 'courier_comparison',
  LOGISTICS_SETTINGS: 'logistics_settings',

  // Customers Sub-modules
  CUSTOMER_360: 'customer_360',
  // COMMS_INBOX: 'comms_inbox',
  TICKETS_FEEDBACK: 'tickets_feedback',
  CAMPAIGNS: 'campaigns',
  CUSTOMER_SETTINGS: 'customer_settings',

  // Campaigns & Integrations Sub-modules
  CAMPAIGN_DASHBOARD: 'campaign_dashboard',
  AUDIENCE_SEGMENTS: 'audience_segments',
  CONTENT_TEMPLATES: 'content_templates',
  SEND_TRACK_CAMPAIGNS: 'send_track_campaigns',
  INTEGRATION_SETTINGS: 'integration_settings',

  // Reports & Analytics Sub-modules
  SALES_CRM_DASHBOARDS: 'sales_crm_dashboards',
  // QUOTE_ORDER_ANALYTICS: 'quote_order_analytics',
  LOGISTICS_OPS_BOARDS: 'logistics_ops_boards',
  PARTY_FINANCIALS: 'party_financials',
  TARGET_ACHIEVEMENT: 'target_achievement',
  REPORT_SETTINGS: 'report_settings',

  // Analytics & Reporting Sub-modules
  ORG_OVERVIEW: 'org_overview',
  HR_DASHBOARDS: 'hr_dashboards',
  SALES_DASHBOARDS: 'sales_dashboards',
  LOGISTICS_DASHBOARDS: 'logistics_dashboards',
  REPORT_LIBRARY: 'report_library',
  REPORT_BUILDER: 'report_builder',
  SCHEDULED_REPORTS: 'scheduled_reports',

  // Employee Self Service Sub-modules
  MY_PROFILE: 'my_profile',
  MY_ATTENDANCE: 'my_attendance',
  APPLY_LEAVE: 'apply_leave',
  MY_PAYSLIPS: 'my_payslips',
  MY_TRAINING: 'my_training',
  MY_PERFORMANCE: 'my_performance',
  SUBMIT_RESIGNATION: 'submit_resignation',

  // Common
  PROFILE: 'profile',
  SETTINGS: 'settings'
};

// Theme Colors
export const THEME_COLORS = {
  PRIMARY: '#22c55e',
  SECONDARY: '#3b82f6',
  SUCCESS: '#10b981',
  WARNING: '#f59e0b',
  ERROR: '#ef4444',
  INFO: '#06b6d4'
};

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  USER_DATA: 'user_data',
  THEME: 'theme',
  SIDEBAR_COLLAPSED: 'sidebar_collapsed'
};

// Role Display Labels
export const ROLE_LABELS = {
  'super_admin': 'Super Admin',
  'hr_manager': 'HR Manager',
  'hr_executive': 'HR Executive',
  'employee': 'Employee',
  'customer': 'Customer',
  'sales_manager': 'Sales Manager',
  'sales_executive': 'Sales Executive',
  'dispatch_manager': 'Dispatch Manager',
  'support_manager': 'Support Manager',
  'trainee': 'Trainee'
};

/**
 * Format role name for display (replaces underscores with spaces and capitalizes)
 * @param {string} role - The role string from database (e.g., 'super_admin')
 * @returns {string} - Formatted role name (e.g., 'Super Admin')
 */
export const formatRoleName = (role) => {
  if (!role) return 'Employee';

  // Return pre-defined label if exists
  if (ROLE_LABELS[role]) {
    return ROLE_LABELS[role];
  }

  // Fallback: format by replacing underscores and capitalizing
  return role
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};
